import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import api, { API } from "../lib/api";
import { ChevronLeft, ChevronRight, Loader2, ShieldCheck } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

export default function PdfReader({ slug, lang = "it", accent = "#F5842B" }) {
  const containerRef = useRef(null);
  const saveTimer = useRef(null);
  const ready = useRef(false);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [width, setWidth] = useState(800);
  const [error, setError] = useState(false);

  const file = useMemo(() => ({ url: `${API}/courses/${slug}/pdf?lang=${lang}`, withCredentials: true }), [slug, lang]);

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) setWidth(Math.min(900, containerRef.current.clientWidth - 4));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    api.get(`/courses/${slug}/bookmark`)
      .then((r) => setPage(r.data.page || 1))
      .catch(() => {})
      .finally(() => { ready.current = true; });
  }, [slug]);

  const saveBookmark = useCallback((p) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.post(`/courses/${slug}/bookmark`, { page: p }).catch(() => {});
    }, 700);
  }, [slug]);

  const goTo = (p) => {
    const clamped = Math.max(1, Math.min(numPages || 1, p));
    setPage(clamped);
    if (ready.current) saveBookmark(clamped);
  };

  if (error) {
    return <div className="rounded-2xl border border-white/10 bg-[#0e131c] p-8 text-center text-slate-400 text-sm">Impossibile caricare il PDF.</div>;
  }

  return (
    <div
      ref={containerRef}
      className="rounded-2xl border border-white/10 bg-[#0e131c] p-3 sm:p-4 select-none"
      onContextMenu={(e) => e.preventDefault()}
      data-testid="pdf-reader"
    >
      <div className="flex items-center justify-between gap-3 mb-3 px-1">
        <span className="flex items-center gap-2 text-[11px] font-mono text-slate-500">
          <ShieldCheck size={13} style={{ color: accent }} /> {numPages ? `${page} / ${numPages}` : "…"}
        </span>
        <div className="flex items-center gap-2">
          <button onClick={() => goTo(page - 1)} disabled={page <= 1}
            className="glass rounded-full h-9 w-9 flex items-center justify-center text-white disabled:opacity-30 hover:border-[#F5842B]/60 transition-colors"
            data-testid="pdf-prev">
            <ChevronLeft size={18} />
          </button>
          <button onClick={() => goTo(page + 1)} disabled={numPages ? page >= numPages : true}
            className="glass rounded-full h-9 w-9 flex items-center justify-center text-white disabled:opacity-30 hover:border-[#F5842B]/60 transition-colors"
            data-testid="pdf-next">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <div className="flex justify-center overflow-auto" style={{ maxHeight: "78vh" }}>
        <Document
          file={file}
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          onLoadError={() => setError(true)}
          loading={<div className="py-24 flex justify-center"><Loader2 className="animate-spin" style={{ color: accent }} /></div>}
        >
          <Page
            pageNumber={page}
            width={width}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            loading={<div className="py-24 flex justify-center"><Loader2 className="animate-spin" style={{ color: accent }} /></div>}
          />
        </Document>
      </div>
    </div>
  );
}
